import type {UnknownAction} from '@reduxjs/toolkit';
import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {match, Pattern} from 'ts-pattern';

import {REHYDRATE_RTK_QUERY} from 'features/rtkq-persist';

import {
  GetAllEnumsApiArg,
  GetAllEnumsResponse,
  GetTrimLevelsApiArg,
  GetTrimLevelsApiResponse,
  GetMakeModelApiArg,
  GetMakeModelResponse,
  GetApiCountriesApiResponse,
  GetApiCountriesApiArg,
} from '../types/api/api';

export const catalogueApi = createApi({
  reducerPath: 'catalogue',
  baseQuery: fetchBaseQuery({baseUrl: process.env.CATALOGUE_URL}),
  keepUnusedDataFor: 60 * 60 * 24, // 1 day
  /**
   * This is crucial for making the rehydrated state work correctly.
   * It tells RTK Query how to extract the rehydrated state from our
   * custom REHYDRATE_RTK_QUERY action.
   */
  extractRehydrationInfo(
    action: UnknownAction & {
      payload?: {
        reducerPath?: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state?: any;
      };
    },
    {reducerPath}
  ) {
    if (action.type === REHYDRATE_RTK_QUERY && action.payload?.reducerPath === reducerPath) {
      return action.payload.state;
    }
  },
  endpoints: (build) => ({
    getAllEnums: build.query<GetAllEnumsResponse, GetAllEnumsApiArg>({
      query: (queryArg) => ({
        url: `/api/enum`,
        params: {
          lang: queryArg.lang,
          order_by: queryArg.orderBy,
          vehicle_type: queryArg.vehicleType,
        },
      }),
    }),
    getMakeModel: build.query<GetMakeModelResponse, GetMakeModelApiArg>({
      query: (queryArg) => {
        const url = match(queryArg.make)
          .with(Pattern.string, () => `/api/make-model/${queryArg.vehicleType}/${queryArg.make}`)
          .with(Pattern.nullish, () => `/api/make-model/${queryArg.vehicleType}`)
          .exhaustive();

        return {
          url,
          params: {lang: queryArg.lang, logo_url: true},
        };
      },
    }),
    getTrimLevels: build.query<GetTrimLevelsApiResponse, GetTrimLevelsApiArg>({
      query: (queryArg) => ({
        url: `/api/trim_levels`,
        params: {
          model_family: queryArg.modelFamily,
          make: queryArg.make,
          country: queryArg.country,
          year_from: queryArg.yearFrom,
          year_to: queryArg.yearTo,
        },
      }),
    }),
    getApiCountries: build.query<GetApiCountriesApiResponse, GetApiCountriesApiArg>({
      query: (queryArg) => ({url: `/api/countries`, params: {format: queryArg.format}}),
    }),
  }),
});

export const {
  useGetAllEnumsQuery,
  useGetMakeModelQuery,
  useGetTrimLevelsQuery,
  useGetApiCountriesQuery,
} = catalogueApi;
